import { useContext, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AuthApi } from '@api';
import { AppContext } from 'src/contexts/AppContext';

/**
 * "Konto" i inställningarna — här bor borttagningen av kontot.
 *
 * Tidigare var "Ta bort konto" en egen röd rubrik mitt i listan, och bekräftelsen var
 * en Ja/Nej-ruta med fokus på Ja: två tryck, eller ett tryck och Enter, räckte för att
 * radera allt. Det är den enda handlingen i appen som inte går att ångra, så den ska
 * kräva ett avsiktligt val i tre led:
 *
 *  1. Den ligger i en hopfälld rubrik med neutral färg, inte som en röd rad bland de
 *     andra — man måste leta upp den.
 *  2. Knappen inne i rubriken är en dämpad textknapp under en mening om vad som händer.
 *  3. Bekräftelsen kräver att man skriver sitt eget användar-ID. Det går inte att göra
 *     av misstag, och det fungerar likadant på alla språk (till skillnad från ett
 *     inskrivet ord som "RADERA"). Avbryt är förvalt, inte Ta bort.
 */
const AccountSettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(AppContext);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [failed, setFailed] = useState(false);

  const tag = user?.tag ?? '';
  const matches = tag !== '' && typed.trim().toLowerCase() === tag.toLowerCase();

  const close = () => {
    if (deleting) return;
    setOpen(false);
    setTyped('');
    setFailed(false);
  };

  const deleteAccount = async () => {
    if (!matches) return;
    setDeleting(true);
    setFailed(false);
    const res = await new AuthApi().deleteAccount();
    if (res) {
      navigate('/login');
      window.location.reload();
      return;
    }
    setDeleting(false);
    setFailed(true);
  };

  return (
    <Stack spacing={1.5} alignItems="flex-start">
      <Typography variant="body2" color="text.secondary">
        {t('settings.account.intro')}
      </Typography>
      <Button
        variant="text"
        color="error"
        size="small"
        onClick={() => setOpen(true)}
        sx={{ textTransform: 'none', fontWeight: 600, px: 0 }}
      >
        {t('settings.account.delete')}
      </Button>

      <Dialog open={open} onClose={close} aria-labelledby="delete-account-title">
        <DialogTitle id="delete-account-title">{t('settings.account.confirmTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText>{t('settings.account.confirmBody')}</DialogContentText>
            <DialogContentText>{t('settings.account.confirmLabel', { tag })}</DialogContentText>
            <TextField
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={tag}
              size="small"
              fullWidth
              autoComplete="off"
              inputProps={{
                'aria-label': t('settings.account.confirmLabel', { tag }) as string,
                autoCapitalize: 'none',
                spellCheck: false,
              }}
            />
            {failed && (
              <Typography variant="body2" color="error">
                {t('settings.account.failed')}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={close} autoFocus disabled={deleting}>
            {t('settings.account.cancel')}
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={deleteAccount}
            disabled={!matches || deleting}
          >
            {t('settings.account.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default AccountSettings;
